import { useState } from 'react';
import { useAccountApi } from '../../api/AccountApiProvider';
import { useAccountSession } from '../../auth/AccountSessionProvider';
import { toApiError } from '../../api/errors';
import type { ApiError } from '../../api/errors';
import type { IdentityChoice, TenancyIdentity } from '../../api/types';
import { useI18n } from '../../i18n/useI18n';
import { Modal } from '../../ui/Modal';
import { Alert, ErrorAlert } from '../../ui/Alert';
import { Button } from '../../ui/primitives';
import { AttributionNotice } from '../../ui/AttributionNotice';
import { useToast } from '../../ui/Toast';
import { IdentityProviderSummary } from './IdentityProviderSummary';

/**
 * The last screen before the cluster is reconfigured.
 *
 * It restates the two things that are otherwise discovered afterwards: new
 * access stops being issued for as long as the change takes, and a provider
 * that turns out not to work locks everyone out of the cluster console — which
 * is why the way back is the screen behind this dialog and not a page inside
 * the cluster.
 *
 * Confirming does not wait for the result. It starts the change and hands back
 * the state it started in; the screen behind then follows it.
 */
export function SwitchIdentityProviderDialog({
  choice,
  status,
  onClose,
  onStarted,
}: {
  choice: IdentityChoice;
  status: TenancyIdentity;
  onClose: () => void;
  onStarted: (next: TenancyIdentity) => void;
}) {
  const { t } = useI18n();
  const api = useAccountApi();
  const { session } = useAccountSession();
  const toast = useToast();

  const [failure, setFailure] = useState<ApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function start() {
    setFailure(null);
    setSubmitting(true);
    try {
      const started = await api.tenancy.chooseIdentity(choice);
      toast(t('toast.changeStarted'));
      onStarted(started);
    } catch (error: unknown) {
      setFailure(toApiError(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={t('switchProvider.title')}
      subtitle={t('switchProvider.subtitle')}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <span className="spacer" />
          <Button variant="primary" disabled={submitting} onClick={() => void start()}>
            {submitting ? `${t('switchProvider.submitting')}…` : t('switchProvider.submit')}
          </Button>
        </>
      }
    >
      <div className="stack">
        {failure !== null && <StartFailure failure={failure} />}

        <div>
          <p className="section-title" style={{ marginTop: 0 }}>
            {t('switchProvider.target')}
          </p>
          <IdentityProviderSummary choice={choice} providerIssuer={status.provider_issuer} />
        </div>

        <Alert tone="warn" title={t('identityProvider.effect.title')}>
          <p>{t('identityProvider.effect.restart')}</p>
          <p style={{ marginTop: 8 }}>{t('identityProvider.effect.pause')}</p>
          <p style={{ marginTop: 8 }}>{t('identityProvider.effect.sessions')}</p>
        </Alert>

        {/* The price was read where the choice was made. Here it is a thing to
            check against, not a thing to learn, so it opens on request. */}

        <Alert tone="info" title={t('switchProvider.wayBack.title')}>
          {status.revertable
            ? t('switchProvider.wayBack.body')
            : t('switchProvider.wayBack.first')}
        </Alert>

        {session !== null && <AttributionNotice actorName={session.actor.audit_name} />}
      </div>
    </Modal>
  );
}

/**
 * The refusals a declaration can meet, and none of them is "something went
 * wrong". The choice is refused at the edge when it names a provider the
 * deployment does not run, a field is named when a field is at fault, and a
 * change already running is refused rather than queued.
 */
function StartFailure({ failure }: { failure: ApiError }) {
  const { t } = useI18n();

  if (failure.code === 'provider_identity_unavailable') {
    return (
      <Alert tone="warn" title={t('identityProvider.provider.unavailable.title')} live>
        {t('identityProvider.provider.unavailable')}
      </Alert>
    );
  }
  if (failure.code === 'change_under_way') {
    return (
      <Alert tone="warn" title={t('identityProvider.underWay.title')} live>
        {t('identityProvider.underWay.body')}
      </Alert>
    );
  }
  if (failure.code === 'invalid_request') {
    const fields = Object.entries(failure.fields);
    return (
      <Alert tone="deny" title={t('identityProvider.refused.title')} live>
        <p>{t('identityProvider.refused.body')}</p>
        {fields.map(([field, reason]) => (
          <p className="mono" key={field} style={{ marginTop: 8 }}>
            {field}
            {': '}
            {reason}
          </p>
        ))}
      </Alert>
    );
  }
  return <ErrorAlert error={failure} />;
}
